import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Archive, ArrowClockwise, CloudArrowUp, CheckCircle, XCircle, ArrowSquareOut,
    PlayCircle, Trash, GoogleLogo, PlusCircle, LinkBreak, FolderOpen, Warning,
} from "@phosphor-icons/react";

const fmtWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function SuperDailyArchive() {
    const [params, setParams] = useSearchParams();
    const [status, setStatus] = useState({ connected: false, oauth_client_configured: false });
    const [rows, setRows] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [testResult, setTestResult] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [alsoDeleteDrive, setAlsoDeleteDrive] = useState(false);
    const [manualDate, setManualDate] = useState(() => {
        const y = new Date(); y.setDate(y.getDate() - 1);
        return y.toISOString().slice(0, 10);
    });
    // v1.27.34 — Stale archives (transactions added after the archive was
    // uploaded, i.e. backdated slips). Auto-refreshed on load; user can bulk
    // re-run to push a fresh Version-N per row.
    const [stale, setStale] = useState([]);
    const [staleBusy, setStaleBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [st, list, staleRes] = await Promise.all([
                api.get("/super/archive/oauth/status"),
                api.get("/super/archive/list", { params: { limit: 300 } }),
                api.get("/super/archive/stale"),
            ]);
            setStatus(st.data);
            setRows(list.data.entries || []);
            setStale(staleRes.data.stale || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (params.get("connected") === "1") {
            toast.success("Google Drive connected successfully");
            setParams({}, { replace: true });
            load();
        } else if (params.get("drive_error")) {
            toast.error(`Drive connect failed: ${params.get("drive_error")}`);
            setParams({}, { replace: true });
        }
    }, [params, setParams, load]);

    const connect = async () => {
        setBusy("connect");
        try {
            const { data } = await api.get("/super/archive/oauth/start");
            window.location.href = data.authorization_url;
        } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect Google Drive? Existing archive files stay on Drive; only the app's connection is removed.")) return;
        setBusy("disc");
        try {
            await api.post("/super/archive/oauth/disconnect");
            toast.success("Disconnected");
            setTestResult(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(""); }
    };

    const openFolderPicker = async () => {
        setBusy("folders");
        try {
            const { data } = await api.get("/super/archive/folders");
            setFolders(data.folders || []);
            setPickerOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(""); }
    };

    const createMdimpexFolder = async () => {
        setBusy("mkf");
        try {
            const { data } = await api.post("/super/archive/folder/create", {});
            toast.success(`Created "${data.folder.name}"`);
            setPickerOpen(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(""); }
    };

    const selectFolder = async (f) => {
        setBusy(`sel-${f.id}`);
        try {
            await api.post("/super/archive/folder/select", { folder_id: f.id, folder_name: f.name });
            toast.success(`Selected "${f.name}"`);
            setPickerOpen(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(""); }
    };

    const testArchive = async () => {
        setBusy("test");
        setTestResult(null);
        try {
            const { data } = await api.post("/super/archive/test");
            setTestResult({ ok: true, link: data.file?.webViewLink, name: data.file?.name });
            toast.success("Test upload successful");
        } catch (e) {
            setTestResult({ ok: false, error: formatApiError(e) });
            toast.error(formatApiError(e));
        } finally { setBusy(""); }
    };

    const runNow = async () => {
        setBusy("run");
        try {
            const { data } = await api.post("/super/archive/run", { business_date: manualDate });
            const c = data.counts || {};
            toast.success(`Archive run · ${c.success} ok · ${c.failed} failed · ${c.companies} companies · ${c.parties} parties`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(""); }
    };

    // v1.27.34 — Push a fresh Version-N for every archive whose ledger has
    // changed since its PDF was uploaded. Idempotent; already-fresh rows are
    // skipped server-side.
    const rerunStale = async () => {
        if (stale.length === 0) { toast.info("No stale archives detected"); return; }
        if (!window.confirm(`Regenerate ${stale.length} stale archive${stale.length === 1 ? "" : "s"} as a new Version-N?\n\nThis captures any backdated slips or corrections you've made since the original run.`)) return;
        setStaleBusy(true);
        try {
            const { data } = await api.post("/super/archive/rerun-stale", {});
            toast.success(`Scanned ${data.scanned} · Re-ran ${data.rerun_ok}${data.rerun_failed ? " · " + data.rerun_failed + " failed" : ""}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setStaleBusy(false); }
    };

    const retry = async (r) => {
        try {
            const { data } = await api.post(`/super/archive/${r.id}/retry`);
            toast[data.archive.status === "success" ? "success" : "error"](
                `Retry ${data.archive.status}${data.archive.error ? " · " + data.archive.error : ""}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const doDelete = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/super/archive/${confirmDel.id}`, { params: { delete_drive_file: alsoDeleteDrive } });
            toast.success(`Archive removed${alsoDeleteDrive ? " + Drive file deleted" : " (Drive file kept)"}`);
            setConfirmDel(null); setAlsoDeleteDrive(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const testDisabled = !status.connected || !status.folder_id || busy === "test";
    const runDisabled = !status.connected || !status.folder_id || !testResult?.ok || busy === "run";

    return (
        <div data-testid="super-daily-archive" className="space-y-5">
            <div className="border-b border-slate-200 pb-4">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-violet-700">
                    <CloudArrowUp size={14} weight="fill" /> Super User only
                </div>
                <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">Daily Archive · Google Drive</h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">
                    Every night at 00:15 UTC, yesterday's Day Book + one Party Ledger per active party gets uploaded to your Google Drive. No files are ever overwritten — re-runs create Version 2, 3, N.
                </p>
            </div>

            {/* --- Setup steps --- */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Setup</div>

                {/* Step 1: OAuth client */}
                <SetupStep
                    n={1}
                    title="OAuth client ready"
                    done={status.oauth_client_configured}
                    body={status.oauth_client_configured
                        ? "Emergent has set up the Google OAuth client for your app."
                        : (<>
                            Emergent Support needs to add <code className="rounded bg-slate-100 px-1">GOOGLE_CLIENT_ID</code> and <code className="rounded bg-slate-100 px-1">GOOGLE_CLIENT_SECRET</code> to your production environment. Copy the ticket text below and email support.
                            <details className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs">
                                <summary className="cursor-pointer font-semibold text-amber-800">📋 Support ticket template (click to expand)</summary>
                                <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px] text-slate-800" data-testid="support-ticket-text">{`Subject: Enable Google Drive OAuth for teleadmin.in

Hi Emergent Support,

We're using MICM Commercial on teleadmin.in and I've picked the "Sign in with Google" option for our Daily Archive feature. I don't want to set up Google Cloud Console myself.

Please:
1. Create a Google Cloud OAuth 2.0 Client (Web application) on Emergent's side for our app.
2. Add these Authorized Redirect URIs (both preview and production):
   - https://teleadmin.in/api/super/archive/oauth/callback
   - ${status.redirect_uri || "<preview-url>/api/super/archive/oauth/callback"}
3. Enable the Google Drive API for the project.
4. Add the following Drive scope to the OAuth consent screen:
   https://www.googleapis.com/auth/drive
5. Add these two env vars to our production and preview backends:
   - GOOGLE_CLIENT_ID
   - GOOGLE_CLIENT_SECRET
6. Also set FRONTEND_URL="https://teleadmin.in" on production so the callback redirects back correctly.

Thanks!
info@teleadmin.in`}</pre>
                                <p className="mt-2 text-[11px] text-amber-700">Send this to <b>support@emergent.sh</b>. Once they reply, this step will flip to a green tick automatically.</p>
                            </details>
                        </>)}
                />

                {/* Step 2: Connect Google */}
                <SetupStep
                    n={2}
                    title="Connect your Google account"
                    done={status.connected}
                    body={status.connected ? (
                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                            Connected as <b>{status.connected_email || "(unknown)"}</b>
                            <Button size="sm" variant="outline" onClick={disconnect} disabled={busy === "disc"} data-testid="arc-disconnect" className="border-rose-200 text-rose-600 hover:bg-rose-50">
                                <LinkBreak size={12} className="mr-1" /> Disconnect
                            </Button>
                        </div>
                    ) : (
                        <Button onClick={connect} disabled={!status.oauth_client_configured || busy === "connect"} data-testid="arc-connect" className="bg-white border border-slate-300 text-slate-800 hover:bg-slate-50 shadow-sm">
                            <GoogleLogo size={16} className="mr-2 text-rose-500" weight="bold" /> {busy === "connect" ? "Redirecting…" : "Connect Google Drive"}
                        </Button>
                    )}
                />

                {/* Step 3: Pick / create folder */}
                <SetupStep
                    n={3}
                    title="Choose a folder in your Drive"
                    done={!!status.folder_id}
                    body={status.folder_id ? (
                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                            <FolderOpen size={14} className="text-amber-500" weight="fill" /> Selected: <b>{status.folder_name || status.folder_id}</b>
                            <Button size="sm" variant="outline" onClick={openFolderPicker} data-testid="arc-change-folder">Change folder</Button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            <Button onClick={openFolderPicker} disabled={!status.connected || busy === "folders"} data-testid="arc-pick-folder"><FolderOpen size={13} className="mr-1.5" /> Choose existing folder</Button>
                            <Button onClick={createMdimpexFolder} disabled={!status.connected || busy === "mkf"} data-testid="arc-create-folder" className="bg-emerald-600 text-white hover:bg-emerald-700"><PlusCircle size={13} className="mr-1.5" /> Create "MDIMPEX Daily Archive" Folder</Button>
                        </div>
                    )}
                />

                {/* Step 4: Test */}
                <SetupStep
                    n={4}
                    title="Test the connection"
                    done={testResult?.ok}
                    body={
                        <div className="flex flex-wrap items-center gap-2">
                            <Button onClick={testArchive} disabled={testDisabled} data-testid="arc-test" className="bg-violet-600 text-white hover:bg-violet-700">
                                <PlayCircle size={13} className="mr-1.5" /> {busy === "test" ? "Testing…" : "Test Archive"}
                            </Button>
                            {testResult?.ok && (
                                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"><CheckCircle size={12} weight="fill" /> Successful{testResult.link && <a href={testResult.link} target="_blank" rel="noreferrer" className="ml-1 underline"><ArrowSquareOut size={11} className="inline" /> open in Drive</a>}</span>
                            )}
                            {testResult?.ok === false && (
                                <span className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700"><XCircle size={12} weight="fill" /> Failed · {testResult.error}</span>
                            )}
                        </div>
                    }
                />
            </div>

            {/* --- Manual run --- */}
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Manual archive — business date</label>
                    <Input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} data-testid="arc-manual-date" className="mt-1" />
                </div>
                <Button onClick={runNow} disabled={runDisabled} data-testid="arc-run-now" className="bg-emerald-600 text-white hover:bg-emerald-700"><PlayCircle size={14} className="mr-1.5" /> {busy === "run" ? "Running…" : "Run Archive Now"}</Button>
            </div>

            {/* v1.27.34 — Stale archive banner. Amber if any archive's ledger
                has changed since its Drive upload — typically a backdated slip. */}
            {stale.length > 0 && (
                <div data-testid="arc-stale-banner" className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm font-bold text-amber-900">
                                <Warning size={16} weight="fill" /> {stale.length} archive{stale.length === 1 ? "" : "s"} out of date
                            </div>
                            <div className="mt-1 text-xs text-amber-800">
                                Fresh transactions were added to these ledgers after their PDF was uploaded to Drive — usually a backdated slip. Re-run to push a new Version-N with the current state.
                            </div>
                            <ul className="mt-2 max-h-40 overflow-y-auto space-y-0.5 text-[11px] text-amber-900">
                                {stale.slice(0, 20).map((s) => (
                                    <li key={s.archive_id} className="font-mono">
                                        <span className="inline-block w-24">{dmY(s.business_date)}</span>
                                        <span className="inline-block w-32 truncate">{s.company_name}</span>
                                        <span className="inline-block w-40 truncate">{s.report_type === "day_book" ? "Day Book" : `Ledger · ${s.party_name || ""}`}</span>
                                        <span className="opacity-70">v{s.version}</span>
                                    </li>
                                ))}
                                {stale.length > 20 && <li className="italic opacity-70">…and {stale.length - 20} more</li>}
                            </ul>
                        </div>
                        <Button
                            data-testid="arc-rerun-stale-btn"
                            onClick={rerunStale}
                            disabled={staleBusy || !status.connected}
                            className="bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                        >
                            <ArrowClockwise size={14} className="mr-1.5" />
                            {staleBusy ? "Re-running…" : `Re-run ${stale.length} stale`}
                        </Button>
                    </div>
                </div>
            )}

            {/* --- Archive list --- */}
            {/* v1.27.87 — Mobile screens (<md) get a card-list layout because the
                8-column table was truncating text into unreadable slivers on phones.
                Desktop (≥md) keeps the exact same table view unchanged. */}
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {/* Desktop table header — hidden on mobile */}
                <div className="hidden md:grid grid-cols-12 gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    <div className="col-span-2">Business date</div>
                    <div className="col-span-2">Company</div>
                    <div className="col-span-1">Type</div>
                    <div className="col-span-2">Party</div>
                    <div className="col-span-2">File</div>
                    <div className="col-span-1">Created</div>
                    <div className="col-span-1">Status</div>
                    <div className="col-span-1 text-right">Actions</div>
                </div>
                {loading && rows.length === 0 && <div className="px-6 py-10 text-center text-sm text-slate-400">Loading…</div>}
                {!loading && rows.length === 0 && <div className="flex flex-col items-center gap-2 px-6 py-14 text-center text-slate-400"><Archive size={28} /><div className="text-sm">No archives yet — complete steps 1-4 above and run once.</div></div>}

                {/* Desktop rows — visible only on md+ */}
                <ul data-testid="arc-list" className="hidden md:block">
                    {rows.map((r) => (
                        <li key={r.id} data-testid={`arc-row-${r.id}`} className="grid grid-cols-12 items-center gap-2 border-t border-slate-100 px-4 py-2 first:border-0 hover:bg-slate-50">
                            <div className="col-span-2 font-mono text-xs text-slate-700">{dmY(r.business_date)}</div>
                            <div className="col-span-2 truncate text-xs text-slate-800" title={r.company_name}>{r.company_name}</div>
                            <div className="col-span-1"><span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${r.report_type === "day_book" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{r.report_type === "day_book" ? "Day Book" : "Ledger"}</span></div>
                            <div className="col-span-2 truncate text-xs text-slate-700" title={r.party_name}>{r.party_name || "—"}</div>
                            <div className="col-span-2 truncate font-mono text-[10px] text-slate-500" title={r.file_name}>{r.file_name} <span className="text-slate-400">· v{r.version}</span></div>
                            <div className="col-span-1 text-[10px] text-slate-500">{fmtWhen(r.created_at)}</div>
                            <div className="col-span-1">
                                {r.status === "success"
                                    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><CheckCircle size={11} weight="fill" /> Success</span>
                                    : <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700" title={r.error || ""}><XCircle size={11} weight="fill" /> Failed</span>}
                            </div>
                            <div className="col-span-1 flex justify-end gap-1">
                                {r.drive_web_view_link && <a href={r.drive_web_view_link} target="_blank" rel="noreferrer" data-testid={`arc-open-${r.id}`}><Button variant="outline" size="sm"><ArrowSquareOut size={12} /></Button></a>}
                                {r.status === "failed" && <Button variant="outline" size="sm" onClick={() => retry(r)} data-testid={`arc-retry-${r.id}`}><ArrowClockwise size={12} /></Button>}
                                <Button variant="outline" size="sm" onClick={() => { setConfirmDel(r); setAlsoDeleteDrive(false); }} data-testid={`arc-del-${r.id}`} className="border-rose-200 text-rose-600 hover:bg-rose-50"><Trash size={12} /></Button>
                            </div>
                        </li>
                    ))}
                </ul>

                {/* Mobile cards — visible only below md */}
                <div className="md:hidden space-y-3 p-3" data-testid="arc-list-mobile">
                    {rows.map((r) => {
                        const isDayBook = r.report_type === "day_book";
                        return (
                            <div
                                key={r.id}
                                data-testid={`arc-card-${r.id}`}
                                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-display text-lg font-bold tracking-tight text-slate-900">
                                            {dmY(r.business_date)}
                                        </div>
                                        <div className="mt-0.5 truncate text-xs font-semibold text-slate-600" title={r.company_name}>
                                            {r.company_name}
                                        </div>
                                    </div>
                                    <span
                                        className={`shrink-0 inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                            isDayBook ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"
                                        }`}
                                    >
                                        {isDayBook ? "Day Book" : "Ledger"}
                                    </span>
                                </div>

                                {(r.party_name || !isDayBook) && (
                                    <div className="mt-2 text-xs">
                                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Party</span>
                                        <div className="truncate text-slate-800" title={r.party_name}>{r.party_name || "—"}</div>
                                    </div>
                                )}

                                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Created</div>
                                        <div className="text-slate-700">{fmtWhen(r.created_at)}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Status</div>
                                        {r.status === "success" ? (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                <CheckCircle size={11} weight="fill" /> Success
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700" title={r.error || ""}>
                                                <XCircle size={11} weight="fill" /> Failed
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-2 truncate font-mono text-[10px] text-slate-500" title={r.file_name}>
                                    {r.file_name} <span className="text-slate-400">· v{r.version}</span>
                                </div>

                                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
                                    {r.drive_web_view_link && (
                                        <a
                                            href={r.drive_web_view_link}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`arc-open-mobile-${r.id}`}
                                            className="flex-1"
                                        >
                                            <Button variant="outline" size="sm" className="w-full border-slate-300 text-slate-800">
                                                <ArrowSquareOut size={13} className="mr-1.5" /> Open
                                            </Button>
                                        </a>
                                    )}
                                    {r.status === "failed" && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => retry(r)}
                                            data-testid={`arc-retry-mobile-${r.id}`}
                                            className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                                        >
                                            <ArrowClockwise size={13} className="mr-1.5" /> Retry
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => { setConfirmDel(r); setAlsoDeleteDrive(false); }}
                                        data-testid={`arc-del-mobile-${r.id}`}
                                        className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                                    >
                                        <Trash size={13} className="mr-1.5" /> Delete
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Folder picker */}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent className="max-w-lg" data-testid="arc-folder-picker">
                    <DialogHeader>
                        <DialogTitle>Choose a Drive folder</DialogTitle>
                        <DialogDescription>Pick an existing folder from your Google Drive, or create the standard "MDIMPEX Daily Archive" folder at the root of your Drive.</DialogDescription>
                    </DialogHeader>
                    <Button onClick={createMdimpexFolder} disabled={busy === "mkf"} data-testid="arc-picker-create" className="w-full justify-start bg-emerald-600 text-white hover:bg-emerald-700"><PlusCircle size={14} className="mr-2" /> Create "MDIMPEX Daily Archive" Folder</Button>
                    <div className="max-h-64 space-y-1 overflow-y-auto rounded border border-slate-200">
                        {folders.length === 0 && <div className="p-3 text-center text-sm text-slate-400">No folders visible at your Drive root.</div>}
                        {folders.map((f) => (
                            <button key={f.id} onClick={() => selectFolder(f)} data-testid={`arc-picker-folder-${f.id}`} className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50">
                                <FolderOpen size={14} className="text-amber-500" weight="fill" /> {f.name}
                            </button>
                        ))}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setPickerOpen(false)}>Cancel</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <DialogContent className="max-w-lg" data-testid="arc-del-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-rose-700">Delete archive entry?</DialogTitle>
                        <DialogDescription>The Drive file stays in your Google Drive unless you tick the box below.</DialogDescription>
                    </DialogHeader>
                    {confirmDel && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs"><div><b>File:</b> <span className="font-mono">{confirmDel.file_name}</span></div><div><b>Company:</b> {confirmDel.company_name} · <b>Business date:</b> {dmY(confirmDel.business_date)} · v{confirmDel.version}</div></div>}
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={alsoDeleteDrive} onChange={(e) => setAlsoDeleteDrive(e.target.checked)} data-testid="arc-del-drive-cb" /> Also permanently delete the file from Google Drive</label>
                    <DialogFooter><Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button><Button onClick={doDelete} data-testid="arc-del-confirm" className="bg-rose-600 text-white hover:bg-rose-700">Delete</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SetupStep({ n, title, body, done }) {
    return (
        <div className={`mt-3 flex gap-3 rounded-lg border p-3 ${done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
            <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${done ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>{done ? <CheckCircle size={14} weight="fill" /> : n}</div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800">{title}</div>
                <div className="mt-1 text-sm text-slate-600">{body}</div>
            </div>
        </div>
    );
}
